import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { sitesApi, type Site } from '@/lib/sites';
import { areasApi, type AreaResponse } from '@/lib/areas';
import { siteControllersApi } from '@/lib/site-controllers';
import { cn } from '@/lib/utils';
import AreaControl from '@/components/areas/AreaControl';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  boundsFromSitesNullable,
  boundsFromMapPlacementsNullable,
  hasFullMapPlacement,
  isLeafletOverlayOrControlClick,
  placementPolygonLatLngs,
  OVAL_POLYGON_SEGMENTS_DISPLAY,
} from './location-map-shared';
import { AreaPlacementLayer, type AreaPlacement } from './AreaPlacementLayer.tsx';
import { SitePlacementLayer, type SitePlacement } from './SitePlacementLayer';

function MapBackgroundClick({ onClick }: { onClick: () => void }) {
  useMapEvents({
    click: (e) => {
      if (isLeafletOverlayOrControlClick(e)) return;
      onClick();
    },
  });
  return null;
}

function MapFocusOnSelection({
  selectedSite,
  siteAreas,
}: {
  selectedSite: Site | null;
  siteAreas: AreaResponse[];
}) {
  const map = useMap();
  useEffect(() => {
    const lat = selectedSite?.latitude;
    const lng = selectedSite?.longitude;
    if (lat == null || lng == null) return;
    const latN = Number(lat);
    const lngN = Number(lng);
    if (Number.isNaN(latN) || Number.isNaN(lngN)) return;
    const b = boundsFromMapPlacementsNullable(siteAreas, OVAL_POLYGON_SEGMENTS_DISPLAY);
    const siteLL = L.latLng(latN, lngN);
    if (b && b.isValid()) {
      b.extend(siteLL);
      map.fitBounds(b, { padding: [28, 28], maxZoom: 18, animate: false });
      return;
    }
    map.setView(siteLL, Math.max(13, map.getZoom() || 13), { animate: false });
  }, [map, selectedSite?.id, selectedSite?.latitude, selectedSite?.longitude, siteAreas]);
  return null;
}

function MapFitToAllSites({
  enabled,
  sites,
}: {
  enabled: boolean;
  sites: Array<Pick<Site, 'id' | 'latitude' | 'longitude'>>;
}) {
  const map = useMap();
  const hasFittedRef = useRef(false);
  const prevEnabledRef = useRef(false);
  const lastSigRef = useRef<string | null>(null);
  const sitesRef = useRef(sites);
  sitesRef.current = sites;
  const sig = useMemo(
    () => sites.map((s) => `${s.id}:${s.latitude}:${s.longitude}`).join('|'),
    [sites],
  );

  const lcLog = useCallback((message: string, extra?: Record<string, unknown>) => {
    if (!import.meta.env.DEV) return;
    console.debug(`[LocationControl][MapFitToAllSites] ${message}`, extra ?? {});
  }, []);

  useEffect(() => {
    const wasEnabled = prevEnabledRef.current;

    if (!enabled) {
      if (wasEnabled) {
        const mapAnyForDisable = map as L.Map & { __llFitAllSitesSig?: string };
        delete mapAnyForDisable.__llFitAllSitesSig;
      }
      prevEnabledRef.current = false;
      return;
    }

    prevEnabledRef.current = true;

    // When returning from a drill-down (enabled: false -> true), allow a fresh fit.
    if (!wasEnabled && enabled) {
      hasFittedRef.current = false;
      const mapAnyForEnable = map as L.Map & { __llFitAllSitesSig?: string };
      // Critical: `sig` often doesn't change when leaving/returning drill-down, but we still need to refit.
      delete mapAnyForEnable.__llFitAllSitesSig;
    }

    // If the site list changes (common: first render is empty, then react-query fills sites),
    // allow a new fit even if we previously "fitted" to an empty/partial set.
    if (lastSigRef.current !== sig) {
      if (lastSigRef.current != null) {
        lcLog('sites signature changed; resetting fit gate', { from: lastSigRef.current, to: sig });
      }
      const mapAnyForSig = map as L.Map & { __llFitAllSitesSig?: string };
      delete mapAnyForSig.__llFitAllSitesSig;
      lastSigRef.current = sig;
      hasFittedRef.current = false;
    }

    let ro: ResizeObserver | undefined;
    const disconnectRo = () => {
      ro?.disconnect();
      ro = undefined;
    };

    const tryFit = () => {
      if (!enabled) return;
      if (!sitesRef.current.length) {
        return;
      }
      const mapAny = map as L.Map & { __llFitAllSitesSig?: string };
      if (mapAny.__llFitAllSitesSig === sig) {
        return;
      }
      if (hasFittedRef.current) {
        return;
      }

      const b = boundsFromSitesNullable(sitesRef.current);
      if (!b || !b.isValid()) {
        lcLog('skip: invalid bounds', {
          sig,
          siteCount: sitesRef.current.length,
        });
        return;
      }

      map.whenReady(() => {
        requestAnimationFrame(() => {
          map.invalidateSize({ pan: false });
          const size = map.getSize?.();
          if (!size || size.x === 0 || size.y === 0) {
            lcLog('defer: map container has zero size (likely hidden tab / not laid out yet)', {
              sig,
              size: size ? { x: size.x, y: size.y } : null,
            });
            return;
          }

          hasFittedRef.current = true;
          mapAny.__llFitAllSitesSig = sig;
          const sw = b.getSouthWest();
          const ne = b.getNorthEast();
          if (ne.equals(sw)) {
            lcLog('setView: single-point bounds', { center: b.getCenter(), zoom: Math.max(13, map.getZoom() || 13), size });
            map.setView(b.getCenter(), Math.max(13, map.getZoom() || 17), { animate: false });
            disconnectRo();
            return;
          }

          lcLog('fitBounds', {
            sig,
            siteCount: sitesRef.current.length,
            size: { x: size.x, y: size.y },
            sw: { lat: sw.lat, lng: sw.lng },
            ne: { lat: ne.lat, lng: ne.lng },
          });
          map.fitBounds(b, { padding: [8, 8], maxZoom: 17, animate: false });
          disconnectRo();
        });
      });
    };

    tryFit();

    const el = map.getContainer?.();
    if (!el || typeof ResizeObserver === 'undefined') {
      return () => {
        disconnectRo();
      };
    }

    ro = new ResizeObserver(() => {
      // If we previously deferred due to 0x0, this is the most likely moment to succeed.
      tryFit();
    });
    ro.observe(el);

    return () => {
      disconnectRo();
    };
  }, [enabled, lcLog, map, sig]);

  return null;
}

export default function LocationControl({ className, mapHeightClassName = 'h-[32rem]' }: { className?: string; mapHeightClassName?: string }) {
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);

  const { data: sites = [] } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.getAll(),
  });

  const selectedSite = useMemo(() => sites.find((s) => s.id === selectedSiteId) ?? null, [sites, selectedSiteId]);

  const { data: controllers = [] } = useQuery({
    queryKey: ['site-controllers', selectedSiteId],
    queryFn: () => siteControllersApi.getBySite(selectedSiteId!),
    enabled: !!selectedSiteId,
  });

  const controllerIdsSig = useMemo(() => controllers.map((c) => c.id).join(','), [controllers]);
  const { data: areas = [] } = useQuery({
    queryKey: ['areas-for-site', selectedSiteId, controllerIdsSig],
    queryFn: async () => {
      if (!controllers.length) return [];
      const lists = await Promise.all(controllers.map((c) => areasApi.getBySiteController(c.id)));
      return lists.flat();
    },
    enabled: !!selectedSiteId && controllers.length > 0,
  });

  const placedAreas = useMemo(() => areas.filter(hasFullMapPlacement), [areas]);
  const selectedArea = useMemo(
    () => (selectedAreaId ? areas.find((a) => a.id === selectedAreaId) ?? null : null),
    [areas, selectedAreaId],
  );

  const selectedAreaControllerOnline = useMemo(() => {
    if (!selectedArea) return false;
    const controller = controllers.find((c) => c.id === selectedArea.siteControllerId);
    return controller?.state != 'offline';
  }, [controllers, selectedArea]);

  const sitesWithCoords = useMemo(
    () => sites.filter((s) => s.latitude != null && s.longitude != null && !Number.isNaN(Number(s.latitude)) && !Number.isNaN(Number(s.longitude))),
    [sites],
  );

  const defaultCenter = useMemo((): [number, number] => {
    if (sitesWithCoords.length === 0) return [0, 0];
    const latSum = sitesWithCoords.reduce((sum, s) => sum + Number(s.latitude), 0);
    const lngSum = sitesWithCoords.reduce((sum, s) => sum + Number(s.longitude), 0);
    return [latSum / sitesWithCoords.length, lngSum / sitesWithCoords.length];
  }, [sitesWithCoords]);

  const mapFitSitesEnabled = !selectedSiteId;

  const onSiteSelect = useCallback((siteId: string) => {
    setSelectedAreaId(null);
    setSelectedSiteId(siteId);
  }, []);

  const onAreaSelect = useCallback((areaId: string) => {
    setSelectedAreaId(areaId);
  }, []);

  return (
    <div className={cn('w-full space-y-3', className)}>
      <div className={cn('w-full rounded-md overflow-hidden border border-border relative z-0', mapHeightClassName)}>
        {selectedSiteId ? (
          <div className="absolute top-2 right-2 z-[1000] pointer-events-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="opacity-50 hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              onClick={() => {
                setSelectedAreaId(null);
                setSelectedSiteId(null);
              }}
            >
              Back to all sites
            </Button>
          </div>
        ) : null}
        <MapContainer center={defaultCenter} zoom={sitesWithCoords.length ? 6 : 2} style={{ height: '100%', width: '100%', zIndex: 0 }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapBackgroundClick
            onClick={() => {
              setSelectedAreaId(null);
              setSelectedSiteId(null);
            }}
          />

          {selectedSite ? <MapFocusOnSelection selectedSite={selectedSite} siteAreas={placedAreas} /> : null}
          {!selectedSite ? <MapFitToAllSites enabled={mapFitSitesEnabled} sites={sitesWithCoords} /> : null}

          {sitesWithCoords.length ? (
            <SitePlacementLayer
              mode="many-dots"
              markerStyle="pin"
              sites={sitesWithCoords.map(
                (s): SitePlacement => ({
                  id: s.id,
                  name: s.name,
                  latitude: Number(s.latitude),
                  longitude: Number(s.longitude),
                }),
              )}
              selectedSiteId={selectedSiteId}
              onSelectSite={(id) => id && onSiteSelect(id)}
            />
          ) : null}

          {selectedSite ? (
            <AreaPlacementLayer
              areas={placedAreas.map(
                (a): AreaPlacement => ({
                  id: a.id,
                  name: a.name,
                  latitude: a.latitude!,
                  longitude: a.longitude!,
                  shape: a.shape!,
                  widthM: a.widthM!,
                  heightM: a.heightM!,
                  orientationDegrees: a.orientationDegrees!,
                }),
              )}
              selectedAreaId={selectedAreaId}
              editable={false}
              onSelectArea={(id) => id && onAreaSelect(id)}
              getPolygonLatLngs={(a) =>
                placementPolygonLatLngs(
                  {
                    latitude: a.latitude,
                    longitude: a.longitude,
                    shape: a.shape,
                    widthM: a.widthM,
                    heightM: a.heightM,
                    orientationDegrees: a.orientationDegrees,
                  },
                  OVAL_POLYGON_SEGMENTS_DISPLAY,
                )
              }
              getPolygonStyle={(_a, _idx, selected) => {
                const color = selected ? '#0ea5e9' : '#2563eb';
                return {
                  color,
                  weight: selected ? 4 : 2,
                  fillColor: color,
                  fillOpacity: 0.14,
                };
              }}
            />
          ) : null}
        </MapContainer>
        {selectedSite ? (
          <div className="pointer-events-none absolute bottom-2 left-2 z-[1000] max-w-[min(24rem,calc(100%-1rem))] bg-transparent px-1 py-0.5">
            <p className="truncate text-md font-bold text-primary drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)]">
              {selectedSite.name}
            </p>
          </div>
        ) : null}
      </div>

      {/* Area control modal */}
      <Dialog open={!!selectedArea} onOpenChange={(open) => !open && setSelectedAreaId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedArea?.name ?? 'Area'}</DialogTitle>
            <DialogDescription>
              View and edit area settings and presets for the selected area.
            </DialogDescription>
          </DialogHeader>
          {selectedArea ? (
            <AreaControl
              area={selectedArea}
              siteId={selectedSite?.id}
              controllerOnline={selectedAreaControllerOnline}
              disabled={!selectedAreaControllerOnline}
              onSetCurrentPreset={() => {
                setSelectedAreaId(null);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
